import { Camera } from 'domain/camera'
import { CameraModel } from 'domain/camera-model'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Stepper } from 'primereact/stepper'
import { StepperPanel } from 'primereact/stepperpanel'
import { InputNumber } from 'primereact/inputnumber'
import { ToggleButton } from 'primereact/togglebutton'
import React, { useRef } from 'react'
import { onInputChange } from 'shared/utils/onInputChange'
import './camera-stepper.scss'

export interface CameraStepperProps {
  visible: boolean
  camera: Camera
  models: CameraModel[]
  submitted: boolean
  isLoading: boolean
  setCamera: (camera: Camera) => void
  saveCamera: () => void
  onHide: () => void
}

export const sanitizeStreamPath = (input: string): string => {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '')
}

const CameraStepper: React.FC<CameraStepperProps> = ({
  visible,
  isLoading,
  camera,
  models,
  submitted,
  setCamera,
  saveCamera,
  onHide,
}) => {
  const stepperRef = useRef<any>(null)

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value
    if (!camera.streamPath || camera.streamPath === sanitizeStreamPath(camera.name)) {
      setCamera({ ...camera, name: newName, streamPath: sanitizeStreamPath(newName) })
    } else {
      setCamera({ ...camera, name: newName })
    }
  }

  const dialogHeader = (
    <div className='cameras-dialog-header'>
      <i className='pi pi-video cameras-dialog-icon' />
      <span>{camera.id ? 'Editar Câmera' : 'Nova Câmera'}</span>
    </div>
  )

  const dialogFooter = (
    <div className='cameras-dialog-footer'>
      <Button
        label='Cancelar'
        icon='pi pi-times'
        outlined
        className='cameras-btn-cancel'
        onClick={onHide}
      />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      style={{ width: '820px' }}
      header={dialogHeader}
      footer={dialogFooter}
      modal
      className='cameras-dialog'
      onHide={onHide}
    >
      <Stepper ref={stepperRef}>

        {/* Step 1 — Informações Básicas */}
        <StepperPanel header='Informações Básicas'>
          <div className='grid pt-2'>
            <div className='col-6'>
              <label htmlFor='name' className='cameras-label'>
                <i className='pi pi-tag' />
                Nome da Câmera *
              </label>
              <InputText
                id='name'
                value={camera.name}
                onChange={handleNameChange}
                required
                autoFocus
                placeholder='Ex: Câmera Entrada Principal'
                className={`cameras-input ${submitted && !camera.name ? 'p-invalid' : ''}`}
              />
              {submitted && !camera.name && (
                <small className='cameras-error'>Nome é obrigatório.</small>
              )}
              {camera.streamPath && (
                <small style={{ color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                  Stream path: <code style={{ color: '#94a3b8' }}>{camera.streamPath}</code>
                </small>
              )}
            </div>

            <div className='col-6'>
              <label htmlFor='model' className='cameras-label'>
                <i className='pi pi-tag' />
                Modelo da Câmera *
              </label>
              <Dropdown
                id='model'
                value={camera.model}
                options={models}
                optionLabel='name'
                onChange={(e) => setCamera({ ...camera, model: e.value })}
                placeholder='Selecione o modelo'
                className={`cameras-dropdown ${submitted && !camera.model?.id ? 'p-invalid' : ''}`}
              />
              {submitted && !camera.model?.id && (
                <small className='cameras-error'>Modelo é obrigatório.</small>
              )}
            </div>

            {camera.id && (
              <div className='col-6'>
                <label className='cameras-label'>
                  <i className='pi pi-power-off' />
                  Status
                </label>
                <ToggleButton
                  checked={camera.isActive}
                  onChange={(e) => setCamera({ ...camera, isActive: e.value })}
                  onLabel='Câmera Ativa'
                  offLabel='Câmera Inativa'
                  onIcon='pi pi-check'
                  offIcon='pi pi-times'
                  style={{ width: '100%', height: '3rem' }}
                />
              </div>
            )}
          </div>

          <div className='flex pt-4 justify-content-end'>
            <Button
              label='Próximo'
              icon='pi pi-arrow-right'
              iconPos='right'
              onClick={() => stepperRef.current?.nextCallback()}
            />
          </div>
        </StepperPanel>

        {/* Step 2 — Configurações de Rede */}
        <StepperPanel header='Configurações de Rede'>
          <div className='grid pt-2'>
            <div className='col-6'>
              <label htmlFor='ipAddress' className='cameras-label'>
                <i className='pi pi-globe' />
                Endereço IP *
              </label>
              <InputText
                id='ipaddress'
                value={camera.ipAddress}
                onChange={(e) => onInputChange(e, 'ipAddress', setCamera)}
                placeholder='192.168.1.100'
                className={`cameras-input ${submitted && !camera.ipAddress ? 'p-invalid' : ''}`}
              />
              {submitted && !camera.ipAddress && (
                <small className='cameras-error'>Endereço IP é obrigatório.</small>
              )}
            </div>

            <div className='col-3'>
              <label htmlFor='httpPort' className='cameras-label'>
                <i className='pi pi-server' />
                Porta HTTP
              </label>
              <InputText
                type='number'
                id='httpPort'
                value={String(camera.httpPort ?? '')}
                onChange={(e) => onInputChange(e, 'httpPort', setCamera)}
                placeholder='80'
                className={`cameras-input ${submitted && !camera.httpPort ? 'p-invalid' : ''}`}
              />
            </div>

            <div className='col-3'>
              <label className='cameras-label'>
                <i className='pi pi-lock' />
                Protocolo
              </label>
              <ToggleButton
                checked={camera.isHttps}
                onChange={(e) => setCamera({ ...camera, isHttps: e.value })}
                onLabel='HTTPS'
                offLabel='HTTP'
                onIcon='pi pi-lock'
                offIcon='pi pi-lock-open'
                style={{ width: '100%', height: '3rem' }}
              />
            </div>
          </div>

          <div className='flex pt-4 justify-content-between'>
            <Button
              label='Voltar'
              severity='secondary'
              icon='pi pi-arrow-left'
              onClick={() => stepperRef.current?.prevCallback()}
            />
            <Button
              label='Próximo'
              icon='pi pi-arrow-right'
              iconPos='right'
              onClick={() => stepperRef.current?.nextCallback()}
            />
          </div>
        </StepperPanel>

        {/* Step 3 — Credenciais & Localização */}
        <StepperPanel header='Credenciais e Localização'>
          <div className='grid pt-2'>
            <div className='col-6'>
              <label htmlFor='username' className='cameras-label'>
                <i className='pi pi-user' />
                Nome de Usuário
              </label>
              <InputText
                id='username'
                value={camera.username}
                onChange={(e) => onInputChange(e, 'username', setCamera)}
                placeholder='Digite o usuário'
                className='cameras-input'
              />
            </div>

            <div className='col-6'>
              <label htmlFor='password' className='cameras-label'>
                <i className='pi pi-key' />
                Senha
              </label>
              <InputText
                id='password'
                type='password'
                value={camera.password ?? ''}
                onChange={(e) => onInputChange(e, 'password', setCamera)}
                placeholder='Digite a senha'
                className='cameras-input'
              />
              {camera.id && (
                <small style={{ color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                  Deixe em branco para manter a senha atual.
                </small>
              )}
            </div>

            <div className='col-12'>
              <label htmlFor='address' className='cameras-label'>
                <i className='pi pi-map' />
                Endereço
              </label>
              <InputText
                id='address'
                value={camera.address ?? ''}
                onChange={(e) => onInputChange(e, 'address', setCamera)}
                placeholder='Ex: Rua das Flores, 123'
                className='cameras-input'
              />
            </div>

            <div className='col-6'>
              <label htmlFor='latitude' className='cameras-label'>
                <i className='pi pi-compass' />
                Latitude
              </label>
              <InputText
                type='number'
                id='latitude'
                value={camera.latitude != null ? String(camera.latitude) : ''}
                onChange={(e) => onInputChange(e, 'latitude', setCamera)}
                placeholder='Ex: -23.5505'
                className='cameras-input'
              />
            </div>

            <div className='col-6'>
              <label htmlFor='longitude' className='cameras-label'>
                <i className='pi pi-compass' />
                Longitude
              </label>
              <InputText
                type='number'
                id='longitude'
                value={camera.longitude != null ? String(camera.longitude) : ''}
                onChange={(e) => onInputChange(e, 'longitude', setCamera)}
                placeholder='Ex: -46.6333'
                className='cameras-input'
              />
            </div>
            <div className='col-6'>
              <label htmlFor='speedLimit' className='cameras-label'>
                <i className='pi pi-gauge' />
                Limite de Velocidade
              </label>
              <InputNumber
                id='speedLimit'
                inputClassName='cameras-input'
                value={camera.speedLimit ?? null}
                onValueChange={(e) => setCamera({ ...camera, speedLimit: e.value ?? undefined })}
                placeholder='Ex: 60'
                suffix=' km/h'
                min={0}
                max={120}
                className='cameras-input-number'
                showButtons={false}
              />
              <small style={{ color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                Opcional — quando configurado, eventos acima desse limite geram alarme automático.
              </small>
            </div>
          </div>

          <div className='flex pt-4 justify-content-between'>
            <Button
              label='Voltar'
              severity='secondary'
              icon='pi pi-arrow-left'
              onClick={() => stepperRef.current?.prevCallback()}
            />
            <Button
              label='Salvar'
              icon='pi pi-check'
              loading={isLoading}
              className='cameras-btn-save'
              onClick={saveCamera}
            />
          </div>
        </StepperPanel>

      </Stepper>
    </Dialog>
  )
}

export default CameraStepper
